/**
 * @fileoverview One row of the My Page target table.
 * @description Shows a target's bank (with a bar in its display-status colour, its location and
 * Tenant ID), target name and period, assignee, current stage with a chip for a sent payment or an
 * open reassignment request, due date, priority and a three-dot actions menu. A closed (Lost or
 * Rejected) target is greyed out and an overdue due date is flagged. Clicking the row, or the bank
 * name, opens the target; the menu offers View plus every action the backend allows the viewer.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import {
  DISPLAY_STATUS_BAR_CLASS,
  PRIORITY_VARIANT,
  STAGE_CHIP_CLASS,
} from '@/features/sales/lib/my-page-status.utils'
import Badge from '@/shared/components/atoms/Badge'
import RowActionsMenu from '@/shared/components/molecules/RowActionsMenu'

import type {
  MyPageTargetAction,
  MyPageTargetRow as MyPageRow,
} from '@/features/sales/types/my-page.types'
import type { RowAction } from '@/shared/components/molecules/RowActionsMenu'

/** @description The table's column widths, shared by its header row and every target row. */
export const MY_PAGE_GRID_COLUMNS =
  'grid-cols-[minmax(170px,2fr)_minmax(130px,1.5fr)_minmax(110px,1.2fr)_minmax(150px,1.5fr)_minmax(95px,0.9fr)_minmax(90px,0.8fr)_44px]'

const CHIP_CLASS = 'inline-flex w-fit items-center rounded-[5px] px-2.5 py-1 text-xs font-semibold'

interface RowPartProps {
  row: MyPageRow
}

/**
 * @component
 * @param {RowPartProps & { onOpen: (targetId: string) => void }} props - The target and the open
 * handler.
 * @param {MyPageRow} props.row - The target to show.
 * @param {(targetId: string) => void} props.onOpen - Called with the target's id when the bank
 * name is clicked.
 */
function BankCell({ row, onOpen }: RowPartProps & { onOpen: (targetId: string) => void }) {
  const { t } = useTranslation()
  const statusLabel = t(`sales.myWork.displayStatuses.${row.displayStatus}`)

  return (
    <div className="flex min-w-0 items-center gap-[11px]">
      {/* `relative` keeps the screen-reader text inside the bar: an absolutely positioned child of a
          non-positioned row would sit outside the scrolling page area and add a page scrollbar. */}
      <span
        title={statusLabel}
        data-testid="my-work-target-status"
        className={`relative block h-[30px] w-1 shrink-0 rounded-sm ${DISPLAY_STATUS_BAR_CLASS[row.displayStatus]}`}
      >
        <span className="sr-only">{statusLabel}</span>
      </span>
      <div className="flex min-w-0 flex-col">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onOpen(row.targetId)
          }}
          data-testid="my-work-name-button"
          className="min-w-0 cursor-pointer truncate text-left text-[13.5px] font-semibold text-ink hover:text-primary hover:underline"
        >
          {row.bankName}
        </button>
        <span className="truncate text-xs text-footer">
          <span data-testid="my-work-location">
            {[row.city, row.state].filter(Boolean).join(', ') || '—'}
          </span>
          {' · '}
          <span data-testid="my-work-tenant-id">{row.tenantCode}</span>
        </span>
      </div>
    </div>
  )
}

/**
 * @component
 * @param {RowPartProps} props - The target.
 * @param {MyPageRow} props.row - The target whose stage, payment and reassignment state to show.
 */
function StatusChips({ row }: RowPartProps) {
  const { t } = useTranslation()

  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <span
        className={`${CHIP_CLASS} ${STAGE_CHIP_CLASS[row.stage.code]}`}
        data-testid="my-work-stage"
      >
        {row.stage.label}
      </span>
      {row.paymentState === 'SENT' && (
        <span
          className={`${CHIP_CLASS} bg-success-bg text-teal`}
          data-testid="my-work-payment-sent"
        >
          {t('sales.myWork.paymentSent')}
        </span>
      )}
      {row.pendingReassignment && (
        <span
          title={t('sales.myWork.pendingReassignment', {
            name: row.pendingReassignment.proposedAssignee.displayName,
          })}
          className={`${CHIP_CLASS} bg-status-warning-bg text-status-warning`}
          data-testid="my-work-pending-reassignment"
        >
          {t('sales.myWork.pendingApproval')}
        </span>
      )}
    </div>
  )
}

interface MyPageTableRowProps {
  row: MyPageRow
  onOpen: (targetId: string) => void
  onAction: (action: MyPageTargetAction, targetId: string) => void
}

/**
 * @component
 * @param {MyPageTableRowProps} props - The target and the handlers.
 * @param {MyPageRow} props.row - The target to show.
 * @param {(targetId: string) => void} props.onOpen - Called with the target's id when the row, the
 * bank name in it or the menu's View is clicked.
 * @param {(action: MyPageTargetAction, targetId: string) => void} props.onAction - Called with the
 * action and the target's id when a menu action is chosen.
 */
function MyPageTableRow({ row, onOpen, onAction }: MyPageTableRowProps) {
  const { t } = useTranslation()
  const closed = row.targetStatus === 'CLOSED'
  const actions: RowAction[] = [
    { key: 'view', label: t('sales.activeClients.view'), onSelect: () => onOpen(row.targetId) },
    ...row.allowedActions.map((action): RowAction => ({
      key: action,
      label: t(`sales.myWork.actions.${action}`),
      onSelect: () => onAction(action, row.targetId),
    })),
  ]

  return (
    <div
      data-testid="my-work-row"
      data-closed={closed ? 'true' : undefined}
      onClick={() => onOpen(row.targetId)}
      className={`grid ${MY_PAGE_GRID_COLUMNS} cursor-pointer items-center gap-3 border-b border-row-border px-4 py-2.5 hover:bg-surface-hover sm:px-5 ${
        closed ? 'opacity-60' : ''
      }`}
    >
      <BankCell row={row} onOpen={onOpen} />
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-[13px] text-cell" data-testid="my-work-target-name">
          {row.name}
        </span>
        <span className="truncate text-xs text-footer" data-testid="my-work-period">
          {row.periodLabel}
        </span>
      </div>
      <span className="truncate text-[13px] text-cell" data-testid="my-work-assignee">
        {row.assignee.displayName}
      </span>
      <StatusChips row={row} />
      <span
        className={`text-[13px] tabular-nums ${
          row.displayStatus === 'OVERDUE' ? 'font-medium text-danger' : 'text-cell'
        }`}
        data-testid="my-work-due-date"
      >
        {row.dueDate}
      </span>
      <div>
        <Badge variant={PRIORITY_VARIANT[row.priority]} data-testid="my-work-priority">
          {t(`sales.myWork.priorities.${row.priority}`)}
        </Badge>
      </div>
      <div className="justify-self-end">
        <RowActionsMenu
          label={t('sales.myWork.rowActions', { name: row.bankName })}
          actions={actions}
        />
      </div>
    </div>
  )
}

export default MyPageTableRow
